import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import api from '../api';

function UserDeletionDialog({ open, onClose, user, onDeleteSuccess }) {
  const [deletingUser, setDeletingUser] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch deletion preview when dialog opens
  useEffect(() => {
    if (open && user) {
      setLoadingPreview(true);
      api.get(`/users/${user.id}/deletion-preview`)
        .then(response => setDeletionPreview(response.data))
        .catch(error => console.error('Error fetching deletion preview:', error))
        .finally(() => setLoadingPreview(false));
    }
  }, [open, user]);

  const handleDeleteUser = async () => {
    if (!user) {
      return;
    }

    setDeletingUser(true);
    try {
      await api.post(`/users/${user.id}/soft-delete`);
      setDeletionPreview(null);
      onDeleteSuccess?.(user);
      onClose();
    } catch (error) {
      console.error('Error soft-deleting user:', error);
      alert('Failed to soft-delete user: ' + (error.response?.data?.error || error.message));
    } finally {
      setDeletingUser(false);
    }
  };

  const handleClose = () => {
    if (!deletingUser) {
      setDeletionPreview(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle sx={{ color: 'error.main' }}>
        Delete User Account
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            WARNING: This action cannot be easily reversed
          </Typography>
          <Typography variant="body2" gutterBottom>
            This will clear authentication and personal data:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
            <li>Email (set to deleted_{'{userId}'}@deleted.local)</li>
            <li>Wallet address (cleared)</li>
            <li>Player ID (randomized)</li>
            <li>Password (cleared)</li>
            <li>User will be banned</li>
            {loadingPreview ? (
              <li>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading linked accounts...
              </li>
            ) : deletionPreview?.linkedAccounts > 0 ? (
              <li><strong>{deletionPreview.linkedAccounts}</strong> linked account(s) will be deleted</li>
            ) : (
              <li>No linked accounts</li>
            )}
          </Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mt: 2, color: 'success.main' }}>
            Game data will be PRESERVED:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
            <li>User record (with ID)</li>
            <li>All inventories and items</li>
            <li>All MOANI transactions</li>
            <li>All auction history</li>
            <li>All datapoints</li>
            <li>All rewards and stakes</li>
          </Box>
        </Alert>
        
        <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
          User: {user?.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ID: {user?.id}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={deletingUser}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteUser}
          color="error"
          variant="contained"
          disabled={deletingUser}
          startIcon={deletingUser ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {deletingUser ? 'Deleting...' : 'Delete Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserDeletionDialog;

